import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  _business_in_lga,
  _get_all_state,
  _get_cities_by_state_code,
  _update_profile,
} from "../common/axios_services";
import { useSelectedLocation } from "./useSelectedLocation";
import { normalizeBusinessType } from "../utils/businessTypeHelpers";

const SEARCH_DEBOUNCE_DELAY = 300;

export const useBusinessListData = ({ businessType }) => {
  const [businessData, setBusinessData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allLgas, setAllLgas] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedLga, setSelectedLga] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [allStates, setAllStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingLgas, setLoadingLgas] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [fullBusinessData, setFullBusinessData] = useState([]);
  const debounceTimer = useRef(null);

  const user = useSelector((state) => state.userProfile.user);
  const { saveLocation } = useSelectedLocation();

  const getLocation = () => {
    const savedLocation = localStorage.getItem("selectedLocation");
    if (savedLocation) {
      const location = JSON.parse(savedLocation);
      return { state: location.state, lga: location.lga };
    } else if (user?.state && user?.lga) {
      return { state: user.state, lga: user.lga };
    } else {
      return { state: "Lagos", lga: "Ikeja" };
    }
  };

  const sortAndFilterBusiness = (data) => {
    if (!data || data.length === 0) return [];
    const location = getLocation();
    const userLga = location?.lga?.toLowerCase();
    return [...data].sort((a, b) => {
      const aOpen = a.isOpen ? 1 : 0;
      const bOpen = b.isOpen ? 1 : 0;
      if (aOpen !== bOpen) {
        return bOpen - aOpen;
      }
      if (userLga) {
        const aMatch = (a.lga || a.city)?.toLowerCase() === userLga ? 1 : 0;
        const bMatch = (b.lga || b.city)?.toLowerCase() === userLga ? 1 : 0;
        if (aMatch !== bMatch) {
          return bMatch - aMatch;
        }
      }
      return (b.rating || 0) - (a.rating || 0);
    });
  };

  const filterBySearch = (data, query) => {
    if (!query.trim()) return data;
    const lowerQuery = query.toLowerCase();
    return data.filter(
      (item) =>
        item.name?.toLowerCase().includes(lowerQuery) ||
        item.description?.toLowerCase().includes(lowerQuery)
    );
  };

  const fetchBusiness = async (lgaName, stateName) => {
    try {
      setLoading(true);
      setError(null);

      let state, lga;
      if (lgaName && stateName) {
        state = stateName;
        lga = lgaName;
      } else if (selectedState) {
        state = selectedState.name;
        lga = selectedLga?.name;
      } else {
        const location = getLocation();
        state = location.state;
        lga = location.lga;
      }

      const normType = normalizeBusinessType(businessType);
      const isStateWideType =
        normType === "wholesale" || businessType === "raw_food";

      const queryLga = isStateWideType ? undefined : lga;

      let currentPage = 1;
      let allFetchedData = [];
      let hasMore = true;

      while (hasMore && currentPage <= 10) {
        const response = await _business_in_lga({
          page: currentPage,
          per_page: 1000,
          state,
          lga: queryLga,
          type: businessType,
        });

        const resBody = response?.data?.data || response?.data || {};
        const pageItems = Array.isArray(resBody?.data)
          ? resBody.data
          : Array.isArray(resBody)
          ? resBody
          : Array.isArray(response?.data)
          ? response.data
          : [];

        allFetchedData = [...allFetchedData, ...pageItems];

        const pagination =
          resBody?.pagination ||
          resBody?.meta ||
          response?.data?.pagination ||
          response?.data?.meta;
        const totalPages =
          pagination?.totalPages ||
          pagination?.last_page ||
          pagination?.lastPage ||
          1;

        if (
          currentPage >= totalPages ||
          pageItems.length === 0 ||
          pageItems.length < 50
        ) {
          hasMore = false;
        } else {
          currentPage++;
        }
      }

      const uniqueData = Array.from(
        new Map(
          allFetchedData.map((item) => [
            item.id || item.businessId || item._id,
            item,
          ])
        ).values()
      );

      const sorted = sortAndFilterBusiness(uniqueData);
      setFullBusinessData(sorted);
      setBusinessData(filterBySearch(sorted, searchQuery));
    } catch (err) {
      console.log({ err });
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeLocation = async () => {
      let currentState = "Lagos";

      const savedLocation = localStorage.getItem("selectedLocation");
      if (savedLocation) {
        const location = JSON.parse(savedLocation);
        setSelectedState({ name: location.state });
        setSelectedLga({ name: location.lga });
        setSelectedLocation(location);
        currentState = location.state;
        await fetchBusiness();
      } else if (user?.state && user?.lga) {
        const stateObj = { name: user.state };
        const lgaObj = { name: user.lga };
        setSelectedState(stateObj);
        setSelectedLga(lgaObj);
        const location = {
          name: user.lga,
          state: user.state,
        };
        setSelectedLocation(location);
        saveLocation(user.state, user.lga);
        currentState = user.state;
        await fetchBusiness();
      } else {
        setIsModalVisible(true);
        await fetchBusiness();
      }

      await Promise.all([fetchStates(), fetchLgas(currentState)]);
    };

    if (user !== null) {
      initializeLocation();
    } else {
      setLoading(false);
    }
  }, [businessType, user]);

  const fetchStates = async () => {
    setLoadingStates(true);
    try {
      const response = await _get_all_state();
      setAllStates(response.data.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchLgas = async (stateName) => {
    if (!stateName) return;
    setLoadingLgas(true);
    try {
      const response = await _get_cities_by_state_code(stateName);
      setAllLgas(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoadingLgas(false);
    }
  };

  const handleStateSelect = (state) => {
    setSelectedState(state);
    setSelectedLga(null);
    setAllLgas([]);
    fetchLgas(state.name);
    fetchBusiness(undefined, state.name);
  };

  const handleLgaSelect = (lga) => {
    setSelectedLga(lga);
    if (selectedState) {
      const location = {
        name: lga.name,
        state: selectedState.name,
      };
      setSelectedLocation(location);
      fetchBusiness();
    }
  };

  const handleLGAChange = async (lgaName) => {
    const lga = allLgas.find((l) => l.name === lgaName);
    if (lga) {
      setSelectedLga(lga);
      const stateName = selectedState?.name || "Lagos";
      const location = {
        name: lga.name,
        state: stateName,
      };
      setSelectedLocation(location);
      saveLocation(stateName, lga);
      
      setIsModalVisible(false);
      fetchBusiness(lga.name, stateName);
    }
  };

  const handleContinue = async () => {
    if (selectedLga && selectedState) {
      const location = {
        name: selectedLga.name,
        state: selectedState.name,
      };
      setSelectedLocation(location);
      saveLocation(selectedState.name, selectedLga);
      
      setIsModalVisible(false);
      fetchBusiness();
    }
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setBusinessData(filterBySearch(fullBusinessData, query));
    }, SEARCH_DEBOUNCE_DELAY);
  };

  return {
    loading,
    error,
    selectedLGA: selectedLga,
    selectedState,
    selectedLocation,
    isModalVisible,
    setIsModalVisible,
    businessData,
    all_lgas: allLgas,
    allStates,
    loadingCities,
    loadingStates,
    loadingLgas,
    handleStateSelect,
    handleLgaSelect,
    handleLGAChange,
    handleContinue,
    searchQuery,
    handleSearchChange,
  };
};
